import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Sparkles, Map, Calendar, Users, Check, ArrowRight, Heart, Compass } from "lucide-react";
import { useEffect } from "react";

const customFeatures = [
  { icon: Sparkles, title: "Truly Personalized", desc: "Every detail tailored to your preferences" },
  { icon: Map, title: "Flexible Routes", desc: "Create your own path, your own way" },
  { icon: Calendar, title: "Your Timeline", desc: "Travel at your pace, on your schedule" },
  { icon: Heart, title: "Special Touches", desc: "Celebrate occasions with unique experiences" },
];

const customExamples = [
  {
    title: "Romantic Escapes",
    desc: "Sunset dinners, private tours, and intimate experiences",
    image: "https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=600&q=80"
  },
  {
    title: "Adventure Expeditions",
    desc: "Off-the-beaten-path adventures and adrenaline activities",
    image: "https://images.unsplash.com/photo-1501555088652-021faa106b9b?w=600&q=80"
  },
  {
    title: "Cultural Immersion",
    desc: "Deep dives into local traditions, cuisine, and history",
    image: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=600&q=80"
  },
];

export default function CustomItinerariesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white" data-testid="custom-itineraries-page">
      <SeoHead
        title="Custom Tour Packages & Itineraries"
        description="Tripsyfy creates custom tour packages and itineraries for families, couples, and groups who want a personalized, guided planning experience."
        canonical="https://tripsyfy.com/services/custom-itineraries"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-accent text-sm font-semibold tracking-wide uppercase mb-4">Our Services</p>
              <h1 className="heading-xl text-primary mb-6">Custom Itineraries</h1>
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                Your dream trip, designed by experts. We create bespoke travel experiences that match your unique style, interests, and pace.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {customFeatures.map((feature, i) => (
                <div key={feature.title} className="bg-white p-6 rounded-lg border border-border text-center">
                  <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-bold text-primary mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* How Custom Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80" 
                  alt="Custom travel planning" 
                  className="rounded-lg w-full h-96 object-cover"
                />
              </div>
              <div>
                <h2 className="heading-lg text-primary mb-6">How Custom Planning Works</h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">1</div>
                    <div>
                      <h3 className="font-bold text-primary mb-1">Share Your Vision</h3>
                      <p className="text-sm text-muted-foreground">Tell us about your dream destinations, interests, budget, and travel style.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">2</div>
                    <div>
                      <h3 className="font-bold text-primary mb-1">Expert Design</h3>
                      <p className="text-sm text-muted-foreground">Our travel designers craft a personalized itinerary with hand-picked experiences.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">3</div>
                    <div>
                      <h3 className="font-bold text-primary mb-1">Refine & Perfect</h3>
                      <p className="text-sm text-muted-foreground">We collaborate with you to fine-tune every detail until it's perfect.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">4</div>
                    <div>
                      <h3 className="font-bold text-primary mb-1">Travel & Enjoy</h3>
                      <p className="text-sm text-muted-foreground">Experience your dream trip with 24/7 support along the way.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Custom Examples */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="heading-lg text-primary mb-4">Custom Trip Ideas</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Whether it's romance, adventure, or culture – we design trips around what matters most to you.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {customExamples.map((example, i) => (
              <AnimatedSection key={example.title} delay={i * 0.1}>
                <div className="bg-white border border-border rounded-lg overflow-hidden hover:border-accent/30 transition-colors group">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={example.image} 
                      alt={example.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-primary mb-2">{example.title}</h3>
                    <p className="text-sm text-muted-foreground">{example.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-lg text-primary-foreground mb-4">Ready to Create Your Perfect Trip?</h2>
            <p className="text-primary-foreground/70 mb-8 max-w-xl mx-auto">
              Share your travel dreams with us and let's design something unforgettable together.
            </p>
            <Link to="/contact">
              <Button variant="navySolid" size="xl" className="gap-2">
                Start Planning <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
